import { readInsurancePrefill } from '@bariendo/lib/insurance-prefill';
import {
  SUBSCRIBER_RELATIONSHIP_CODESYSTEM,
  SUBSCRIBER_RELATIONSHIPS,
  SubscriberRelationship,
} from '@bariendo/lib/insurance-coverage';
import { formatHumanName, MedplumClient } from '@medplum/core';
import { Coverage, Organization, Patient, Reference, RelatedPerson, ResourceType } from '@medplum/fhirtypes';
import { canSearchResourceType } from '@/services/permissions';
import { findInsuranceCoverage } from '@/components/Timeline/TimelineItems/PatientAccessCall/useInsuranceCard';

/** The patient's insurance card as the section shows it. */
export interface InsuranceDetails {
  /** The payor insurer, when the role can read it and it still exists. */
  insurer?: Organization;
  /** The payor is the Patient: signup's "Mine isn't listed". */
  insurerNotListed: boolean;
  memberId?: string;
  groupNumber?: string;
  /** The patient's relationship to the subscriber, `Coverage.relationship`. */
  relationship?: SubscriberRelationship;
  /** The policyholder when the plan isn't the patient's own, if the role can read it. */
  policyholder?: RelatedPerson;
}

/**
 * - `forbidden`: the role's policy has no Coverage search; the section hides.
 * - `none`: no active insurance Coverage; the section hides.
 * - `unreadable`: the read failed; never taken for `none`.
 */
export type InsuranceSectionRead =
  | { status: 'forbidden' | 'none' | 'unreadable' }
  | { status: 'found'; details: InsuranceDetails };

/**
 * Reads the insurance Coverage the `update-insurance-coverage` bot edits, then its payor and
 * policyholder. A role that can't read the payor or policyholder (Finance has no RelatedPerson)
 * still gets the Coverage's own fields.
 */
export async function readInsuranceSection(medplum: MedplumClient, patient: Patient): Promise<InsuranceSectionRead> {
  if (!canSearchResourceType(medplum, 'Coverage')) return { status: 'forbidden' };
  let coverage: Coverage | undefined;
  try {
    coverage = await findInsuranceCoverage(medplum, patient);
  } catch {
    return { status: 'unreadable' };
  }
  if (!coverage) return { status: 'none' };

  const payor = coverage.payor?.[0];
  const relationship = relationshipOf(coverage);
  const [insurer, policyholder] = await Promise.all([
    readIfAllowed(medplum, 'Organization', payor as Reference<Organization> | undefined),
    relationship && relationship !== 'self'
      ? readIfAllowed(medplum, 'RelatedPerson', coverage.subscriber as Reference<RelatedPerson> | undefined)
      : undefined,
  ]);
  const { memberId, groupNumber } = readInsurancePrefill(coverage, undefined);
  return {
    status: 'found',
    details: {
      insurer,
      insurerNotListed: !!payor?.reference?.startsWith('Patient/'),
      memberId,
      groupNumber,
      relationship,
      policyholder,
    },
  };
}

function relationshipOf(coverage: Coverage): SubscriberRelationship | undefined {
  const code = coverage.relationship?.coding?.find((c) => c.system === SUBSCRIBER_RELATIONSHIP_CODESYSTEM)?.code;
  return SUBSCRIBER_RELATIONSHIPS.find((relationship) => relationship === code);
}

async function readIfAllowed<T extends Organization | RelatedPerson>(
  medplum: MedplumClient,
  resourceType: T['resourceType'] & ResourceType,
  reference: Reference<T> | undefined
): Promise<T | undefined> {
  if (!reference?.reference?.startsWith(`${resourceType}/`)) return undefined;
  if (!canSearchResourceType(medplum, resourceType)) return undefined;
  try {
    return await medplum.readReference(reference);
  } catch {
    return undefined;
  }
}

export const NOT_PROVIDED = 'Not provided';
export const NO_GROUP = 'None';
export const INSURER_NOT_LISTED = 'Not listed';
export const SELF = 'Self';

/**
 * Who the policyholder is to the patient. `Coverage.relationship` is the patient's relationship
 * to the subscriber, so a patient coded `child` is on a parent's plan.
 */
export const POLICYHOLDER_LABEL: Record<Exclude<SubscriberRelationship, 'self'>, string> = {
  spouse: 'spouse',
  child: 'parent',
  other: 'other',
};

export interface RowValue {
  text: string;
  /** Nothing on file: shown faint. */
  muted: boolean;
}

export interface InsuranceRows {
  insurer: RowValue;
  memberId: RowValue;
  group: RowValue;
  subscriber: RowValue;
}

const value = (text: string): RowValue => ({ text, muted: false });
const missing = (text: string): RowValue => ({ text, muted: true });

/**
 * What each row reads. A skipped card has no member ID, and its group and subscriber read
 * "Not provided" with it; a card entered without a group reads "None".
 */
export function insuranceRows(details: InsuranceDetails): InsuranceRows {
  const insurerName = details.insurer?.name;
  return {
    insurer: insurerName
      ? value(insurerName)
      : details.insurerNotListed
        ? missing(INSURER_NOT_LISTED)
        : missing(NOT_PROVIDED),
    memberId: details.memberId ? value(details.memberId) : missing(NOT_PROVIDED),
    group: details.groupNumber
      ? value(details.groupNumber)
      : details.memberId
        ? missing(NO_GROUP)
        : missing(NOT_PROVIDED),
    subscriber: subscriberRow(details),
  };
}

function subscriberRow({ relationship, policyholder }: InsuranceDetails): RowValue {
  if (!relationship) return missing(NOT_PROVIDED);
  if (relationship === 'self') return value(SELF);
  const name = policyholder?.name?.[0] ? formatHumanName(policyholder.name[0]) : undefined;
  const birthDate = formatPolicyholderBirthDate(policyholder?.birthDate);
  return value([name, POLICYHOLDER_LABEL[relationship], birthDate && `DoB ${birthDate}`].filter(Boolean).join(' · '));
}

/**
 * `YYYY-MM-DD` as `MM/DD/YYYY`, as the spec shows the policyholder's (About's `formatBirthDate`
 * adds the age, which is the patient's). Read as text so no timezone can shift the day.
 */
export function formatPolicyholderBirthDate(date: string | undefined): string | undefined {
  const match = date?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[2]}/${match[3]}/${match[1]}` : undefined;
}
